import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useClipboard } from '@/hooks/useClipboard';
import { useIsInternalUser } from '@/hooks/useIsInternalUser';
import {
  Accordion,
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { Button } from '@/lib/base/buttons/button';
import { Checkbox } from '@/lib/base/forms/checkbox';
import { TextArea } from '@/lib/base/forms/textarea/textarea';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import type {
  DiagnosticsBundle,
  DiagnosticsScreenshot,
  DiagnosticsTrigger,
  ScreenshotCaptureStatus,
} from '@/lib/diagnostics/collectBundle';
import { collectDiagnosticsBundle } from '@/lib/diagnostics/collectBundle';
import { captureDiagnosticsScreenshot } from '@/lib/diagnostics/screenshotCapture';
import { isApiError } from '@/lib/errors';
import { apiPost } from '@/lib/http/client';
import { logger } from '@/lib/logger';

const DIAGNOSTICS_POST_TIMEOUT_MS = 10000;
const DIAGNOSTICS_ENDPOINT = '/api/diagnostics/report/';
const MESSAGE_MAX_LENGTH = 2000;
// Mirrors the server's 1 MiB cap; the margin covers the data-URI prefix and JSON punctuation.
const MAX_DIAGNOSTICS_BODY_BYTES = 1024 * 1024;
const DIAGNOSTICS_BUDGET_SAFETY_MARGIN_BYTES = 4 * 1024;

type SendFailureReason = 'too-large' | 'rate-limited' | 'server' | 'network';

const SEND_FAILURE_MESSAGES: Record<SendFailureReason, string> = {
  'too-large':
    'This report is too large to send. If you added a screenshot, try again without it, or copy it and email support@ahoy.ai.',
  'rate-limited':
    "You've sent several reports recently. Try again in about an hour.",
  server:
    "We couldn't send your report. Copy it and email support@ahoy.ai instead.",
  network:
    "We couldn't send your report – the network may be blocking Ahoy. Copy it and email support@ahoy.ai instead.",
};

// SPA routes are /<org_id>/... — derive the org from the URL so the report
// is org-attributed server-side (the backend verifies membership).
const ORG_ID_PATH_PATTERN = /^\/(\d+)(?:\/|$)/;

function getCurrentOrgId(): number | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const match = ORG_ID_PATH_PATTERN.exec(window.location.pathname);
  return match ? Number(match[1]) : null;
}

// Reserves room for the actual bundle (including a large recentRequests list) and a
// full-length message, so the image can never push the body over the 1 MiB cap.
function computeMaxImageBytes(bundle: DiagnosticsBundle): number {
  const nonImageBody = {
    payload: { ...bundle, capture: { screenshot: 'included' as const } },
    build_version: bundle.build.version,
    org_id: 0,
    // '中' (U+4E2D) is 1 UTF-16 code unit (counts once toward MESSAGE_MAX_LENGTH)
    // but encodes to 3 UTF-8 bytes — the worst-case bytes-per-.length-unit for any
    // string bounded by JS string length, so this reflects the true worst-case body size.
    message: '中'.repeat(MESSAGE_MAX_LENGTH),
  };
  const nonImageBytes = new Blob([JSON.stringify(nonImageBody)]).size;
  const base64Budget =
    MAX_DIAGNOSTICS_BODY_BYTES -
    nonImageBytes -
    DIAGNOSTICS_BUDGET_SAFETY_MARGIN_BYTES;
  // base64 inflates decoded bytes by 4/3, hence the * 3/4.
  return Math.max(0, Math.floor((base64Budget * 3) / 4));
}

function getScreenshotIssueMessage(
  status: ScreenshotCaptureStatus
): string | null {
  switch (status) {
    case 'failed':
      return "We couldn't capture a screenshot – your report will still send.";
    case 'too_large':
      return 'The screenshot was too large to include – your report will still send.';
    case 'unsupported':
      return "Your browser can't capture a screenshot – your report will still send.";
    default:
      return null;
  }
}

export interface DiagnosticsReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Where the report was initiated from — recorded in the bundle. */
  trigger: DiagnosticsTrigger;
}

export function DiagnosticsReportModal({
  isOpen,
  onClose,
  trigger,
}: DiagnosticsReportModalProps) {
  const [bundle, setBundle] = useState<DiagnosticsBundle | null>(null);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<
    'collecting' | 'ready' | 'sending' | 'sent' | 'failed' | 'collection-failed'
  >('collecting');
  const [copyError, setCopyError] = useState(false);
  const [screenshotChecked, setScreenshotChecked] = useState(false);
  const [screenshotStatus, setScreenshotStatus] =
    useState<ScreenshotCaptureStatus>('declined');
  const [screenshotData, setScreenshotData] =
    useState<DiagnosticsScreenshot | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [reportId, setReportId] = useState<string | null>(null);
  const [failureReason, setFailureReason] =
    useState<SendFailureReason>('network');
  const { copied, copy } = useClipboard();
  const isInternal = useIsInternalUser();
  // Bumped on every open/close so an in-flight POST from a previous session
  // can't flip a freshly collected bundle to 'sent'/'failed'.
  const sessionRef = useRef(0);
  // Tracks whether a bundle has already been collected this open session, so
  // the re-collection triggered by isInternal settling doesn't flip a ready
  // preview back to the collecting spinner. A ref (not the bundle state
  // itself) avoids the effect re-running on its own setBundle call.
  const hasBundleRef = useRef(false);
  // Bumped on EVERY toggle (both tick and untick) so a capture that resolves
  // after the user unchecked the box is silently dropped.
  const toggleRef = useRef(0);

  useEffect(() => {
    sessionRef.current += 1;
    if (!isOpen) {
      setBundle(null);
      setMessage('');
      setStatus('collecting');
      setCopyError(false);
      hasBundleRef.current = false;
      setScreenshotChecked(false);
      setScreenshotStatus('declined');
      setScreenshotData(null);
      setCapturing(false);
      setReportId(null);
      setFailureReason('network');
      return;
    }
    let cancelled = false;
    if (!hasBundleRef.current) {
      setStatus('collecting');
      setScreenshotChecked(false);
      setScreenshotStatus('declined');
      setScreenshotData(null);
      setCapturing(false);
    }
    collectDiagnosticsBundle(trigger, {
      isInternalReporter: isInternal === true,
    })
      .then(b => {
        if (!cancelled) {
          hasBundleRef.current = true;
          setBundle(b);
          setStatus('ready');
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          logger.error('Diagnostics collection failed', error as Error, {
            feature: 'diagnostics',
            phase: 'collection',
          });
          setStatus('collection-failed');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, trigger, isInternal]);

  const handleToggleScreenshot = useCallback(
    async (checked: boolean) => {
      // Increment BEFORE any early return so that unticking while a capture is
      // in flight still bumps the counter and lets the stale await detect staleness.
      toggleRef.current += 1;
      const toggle = toggleRef.current;

      setScreenshotChecked(checked);
      if (!checked) {
        setScreenshotStatus('declined');
        setScreenshotData(null);
        setCapturing(false);
        return;
      }
      if (!bundle) {
        return;
      }
      const session = sessionRef.current;
      setCapturing(true);
      const res = await captureDiagnosticsScreenshot(
        computeMaxImageBytes(bundle)
      );
      if (sessionRef.current !== session || toggleRef.current !== toggle) {
        return;
      }
      setScreenshotStatus(res.status);
      setScreenshotData(res.status === 'included' ? res.screenshot : null);
      setCapturing(false);
    },
    [bundle]
  );

  const outgoingPayload = useMemo(() => {
    if (!bundle) {
      return null;
    }
    const capture = { ...(bundle.capture ?? {}), screenshot: screenshotStatus };
    return {
      ...bundle,
      capture,
      ...(screenshotData ? { screenshot: screenshotData } : {}),
    };
  }, [bundle, screenshotStatus, screenshotData]);

  const previewText = useMemo(
    () => (outgoingPayload ? JSON.stringify(outgoingPayload, null, 2) : ''),
    [outgoingPayload]
  );

  const handleOpenChange = useCallback(
    (o: boolean) => {
      if (!o) {
        onClose();
      }
    },
    [onClose]
  );

  const handleSend = useCallback(async () => {
    if (!bundle || !outgoingPayload) {
      return;
    }
    const session = sessionRef.current;
    setStatus('sending');
    const orgId = getCurrentOrgId();
    const trimmedMessage = message.trim();
    try {
      const result = await apiPost<{ report_id?: string }>(
        DIAGNOSTICS_ENDPOINT,
        {
          payload: outgoingPayload,
          build_version: bundle.build.version,
          ...(orgId !== null && { org_id: orgId }),
          ...(trimmedMessage !== '' && { message: trimmedMessage }),
        },
        DIAGNOSTICS_POST_TIMEOUT_MS
      );
      if (sessionRef.current === session) {
        setReportId(result?.report_id ?? null);
        setStatus('sent');
      }
    } catch (error: unknown) {
      if (sessionRef.current === session) {
        let reason: SendFailureReason = 'network';
        if (isApiError(error)) {
          if (error.is(413)) {
            reason = 'too-large';
          } else if (error.is(429)) {
            reason = 'rate-limited';
          } else {
            reason = 'server';
          }
        }
        setFailureReason(reason);
        setStatus('failed');
      }
    }
  }, [bundle, outgoingPayload, message]);

  const handleCopy = useCallback(async () => {
    if (!previewText) {
      return;
    }
    // Include the typed message so it isn't lost on the email fallback path.
    const trimmedMessage = message.trim();
    const text =
      trimmedMessage === ''
        ? previewText
        : `Message: ${trimmedMessage}\n\n${previewText}`;
    const res = await copy(text);
    setCopyError(!res.success);
  }, [copy, previewText, message]);

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={handleOpenChange}>
      <Modal className='max-w-2xl'>
        <Dialog
          title='Report a problem'
          description='Send a report to Ahoy support to help us figure out what went wrong.'
          footer={
            <div className='flex flex-col gap-3'>
              <div className='flex items-center gap-3'>
                <Button
                  color='primary'
                  onClick={handleSend}
                  isDisabled={
                    (status !== 'ready' && status !== 'failed') || capturing
                  }
                  isLoading={status === 'sending'}
                >
                  Send to Ahoy
                </Button>
                {status === 'sent' && (
                  <Button color='secondary' onClick={onClose}>
                    Close
                  </Button>
                )}
              </div>
              {status === 'sent' && reportId !== null && (
                <p className='text-sm text-[var(--color-text-success-primary)]'>
                  Thanks, your report is on its way. Your reference code is{' '}
                  <span className='font-mono text-[var(--color-text-primary)]'>
                    {reportId}
                  </span>
                  . Quote it if you get in touch with support.
                </p>
              )}
              {status === 'sent' && reportId === null && (
                <p className='text-sm text-[var(--color-text-success-primary)]'>
                  Thanks, your report is on its way. Our team will follow up by
                  email.
                </p>
              )}
              {status === 'failed' && (
                <div className='flex flex-col gap-2'>
                  <p className='text-sm text-[var(--color-text-error-primary)]'>
                    {SEND_FAILURE_MESSAGES[failureReason]}
                  </p>
                  <div className='flex items-center gap-3'>
                    <Button color='secondary' onClick={handleCopy}>
                      {copied ? 'Copied' : 'Copy to clipboard'}
                    </Button>
                    {copyError && (
                      <p className='text-sm text-[var(--color-text-error-primary)]'>
                        Couldn&apos;t copy. Select the text above and copy
                        manually.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          }
        >
          {status === 'collecting' && (
            <div
              role='status'
              aria-live='polite'
              className='flex flex-col items-center gap-3 py-8'
            >
              <LoadingIndicator size='sm' />
              <p className='text-sm text-[var(--color-text-secondary)]'>
                Collecting diagnostics…
              </p>
            </div>
          )}
          {status === 'collection-failed' && (
            <div role='alert' className='flex flex-col items-center gap-3 py-8'>
              <p className='text-sm text-[var(--color-text-error-primary)]'>
                Couldn&apos;t collect diagnostics. Copy the URL and any visible
                error, then email support@ahoy.ai.
              </p>
            </div>
          )}
          {status !== 'collecting' && status !== 'collection-failed' && (
            <div className='flex flex-col gap-3'>
              <TextArea
                label='What happened?'
                placeholder='Tell us what you were trying to do and what went wrong (optional)'
                value={message}
                onChange={setMessage}
                maxLength={MESSAGE_MAX_LENGTH}
                rows={3}
                isDisabled={status === 'sending' || status === 'sent'}
              />
              <Checkbox
                isSelected={screenshotChecked}
                onChange={handleToggleScreenshot}
                isDisabled={status === 'sending' || status === 'sent'}
                label='Include a screenshot of the current page'
              />
              {capturing && (
                <p
                  role='status'
                  aria-live='polite'
                  className='text-sm text-[var(--color-text-secondary)]'
                >
                  Capturing screenshot…
                </p>
              )}
              {screenshotChecked &&
                screenshotStatus === 'included' &&
                screenshotData && (
                  <>
                    <img
                      src={screenshotData.dataUri}
                      alt='Preview of the screenshot that will be sent'
                      className='max-h-60 max-w-full w-auto rounded-lg border border-[var(--color-border-primary)]'
                    />
                    <p className='text-sm text-[var(--color-text-secondary)]'>
                      Screenshot size:{' '}
                      {Math.round(screenshotData.byteLength / 1024)} KB
                    </p>
                  </>
                )}
              {screenshotChecked &&
                !capturing &&
                (screenshotStatus === 'failed' ||
                  screenshotStatus === 'too_large' ||
                  screenshotStatus === 'unsupported') && (
                  <p
                    role='status'
                    aria-live='polite'
                    className='text-sm text-[var(--color-text-secondary)]'
                  >
                    {getScreenshotIssueMessage(screenshotStatus)}
                  </p>
                )}
              {screenshotChecked && screenshotStatus === 'included' ? (
                <p className='text-sm text-[var(--color-text-secondary)]'>
                  The report includes your note above, connection test results,
                  app version, browser info, the addresses of recent requests,
                  and a picture of the current page – which can include the
                  contacts and messages shown on screen. It never includes your
                  passwords.
                </p>
              ) : (
                <p className='text-sm text-[var(--color-text-secondary)]'>
                  The report includes your note above, connection test results,
                  app version, browser info, and the addresses of recent
                  requests – never your emails, contacts, or passwords.
                </p>
              )}
              <Accordion>
                <AccordionItem>
                  <AccordionTrigger className='flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'>
                    <AccordionChevron variant='leading' />
                    See exactly what will be sent
                  </AccordionTrigger>
                  <AccordionPanel className='pt-2'>
                    <pre className='max-h-60 overflow-auto whitespace-pre-wrap rounded-lg bg-[var(--color-bg-secondary)] p-3 text-xs text-[var(--color-text-secondary)]'>
                      {previewText}
                    </pre>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </div>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
